import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableContainerProps,
  TableHead,
  TableRow,
} from '@mui/material'
import { NoResultsAlert } from '../notification/NoResultsAlert'
import { AuditTrailData, TableColumn } from '@/utilities/types'
import { styled, textTransform } from '@mui/system'
import dayjs from 'dayjs'
import { FC } from 'react'
import { AuditTableProps } from '@/utilities/types/table-props'
import { isEmpty } from 'lodash'
import CheckIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/HighlightOff'

const StyledTableContainer = styled(TableContainer)<TableContainerProps>({
  marginTop: '16px',
  backgroundColor: '#FFF',
  padding: '0px 16px 16px 16px',
  borderRadius: '4px',
  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
  maxHeight: '60vh',
  height: '60vh',
  overflowY: 'auto',
  overflowX: 'auto',

  '& table': {
    minWidth: 600,
  },

  '@media (max-width: 768px)': {
    '& table': {
      fontSize: '0.75rem',
    },
    '& th, & td': {
      fontSize: '0.7rem',
      whiteSpace: 'nowrap',
    },
  },
})

const TableHeadStyled = styled(TableHead)({
  height: '50px',
  borderRadius: '6px',
  fontFamily: 'sans-serif',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  backgroundColor: '#F7F8FA',
})

const TableBodyStyled = styled(TableBody)({
  '& tr:nth-of-type(odd)': {
    backgroundColor: '#F7F8FA',
  },
  '& tr:nth-of-type(even)': {
    backgroundColor: 'white',
  },
  '& tr:hover': {
    backgroundColor: '#f5f5f5',
  },
  '& td': {
    borderBottom: 'none',
    fontSize: '0.75rem',
  },
})

const StyledTableCell = styled(TableCell)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowY: 'auto', // key to avoid overflowing
})

const columns: TableColumn[] = [
  { id: 'timestamp', label: 'Timestamp', width: '20%', sortable: false },
  { id: 'data', label: 'Data', width: '20%', sortable: false },
  { id: 'oldValue', label: 'Old Value', width: '20%', sortable: false },
  { id: 'newValue', label: 'New Value', width: '20%', sortable: false },
  { id: 'user', label: 'User', width: '20%', sortable: false },
]

const formatLabel = (key: string) => {
  const customLabels: Record<string, string> = {
    isAwarded: 'Awarded',
  }

  const acronyms = ['COC', 'PO', 'ID', 'PDF'] // Add more acronyms here as needed

  if (customLabels[key]) return customLabels[key]

  let formatted = key
    .replace(/\(.*?\)/g, '') // remove text in parentheses
    .replace(/_/g, ' ') // underscores to spaces
    .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase to space
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // split ALLCAPS followed by CapitalCase
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase()) // capitalize first letters

  // Post-process acronyms to restore correct casing
  acronyms.forEach((acronym) => {
    const regex = new RegExp(
      `\\b${acronym.charAt(0)}${acronym.slice(1).toLowerCase()}\\b`,
      'g',
    )
    formatted = formatted.replace(regex, acronym)
  })

  return formatted
}

const formatUser = (value: string) => {
  if (!value) return ''

  const normalized = /^[A-Z_]+$/.test(value) ? value.toLowerCase() : value

  const spaced = normalized.replace(/([a-z])([A-Z])/g, '$1 $2')

  return spaced
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export const AuditTrailTable: FC<AuditTableProps> = ({
  data = [],
  isLoading,
}) => {
  const isValidUrl = (val: string) => {
    try {
      const url = new URL(val)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch (_) {
      return false
    }
  }

  const isEmpty = (val: any): boolean =>
    val == null || (typeof val === 'object' && Object.keys(val).length === 0)

  const renderValue = (val: any): React.ReactNode => {
    if (val === null || val === undefined || val === '-') {
      return <em>No Data</em>
    }

    if (typeof val === 'boolean') {
      return (
        <>
          {val ? (
            <CheckIcon
              color="success"
              fontSize="inherit"
              sx={{ fontSize: 16, verticalAlign: 'text-bottom' }}
            />
          ) : (
            <CloseIcon
              color="error"
              fontSize="inherit"
              sx={{ fontSize: 16, verticalAlign: 'text-bottom' }}
            />
          )}
        </>
      )
    }
    if (typeof val === 'string' && isValidUrl(val)) {
      return (
        <a
          href={val}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            maxWidth: '130px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            verticalAlign: 'bottom',
          }}
          title={val}
        >
          {val}
        </a>
      )
    }

    if (Array.isArray(val)) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {val.map((item, idx) => (
            <div key={idx}>{renderValue(item)}</div>
          ))}
        </div>
      )
    }
    if (typeof val === 'object' && val !== null) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {Object.entries(val ?? {})
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, nestedVal]) => (
              <div key={key}>
                <strong
                  style={{ textTransform: 'capitalize', fontWeight: 500 }}
                >
                  {formatLabel(key)}: {''}
                </strong>
                {renderValue(nestedVal)}
              </div>
            ))}
        </div>
      )
    }

    return String(val)
  }

  return (
    <StyledTableContainer component={Paper}>
      <Table stickyHeader sx={{ flexGrow: 1, backgroundColor: 'white' }}>
        <NoResultsAlert open={false} onClose={() => {}} />
        <TableHeadStyled>
          <TableRow>
            {columns.map((value, index) => (
              <StyledTableCell
                width={value.width}
                key={index}
                className={value.id}
              >
                {value.label}
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHeadStyled>
        <TableBodyStyled>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '45vh',
                  }}
                >
                  <CircularProgress />
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            data.map((value: AuditTrailData, index: number) => (
              <TableRow key={index}>
                <StyledTableCell>
                  {dayjs(value.dateCreated).format('MM/DD/YYYY hh:mm:ss A')}
                </StyledTableCell>
                <StyledTableCell>
                  {value.datas.map((data) => data)}
                </StyledTableCell>
                <StyledTableCell>{renderValue(value.oldValue)}</StyledTableCell>
                <StyledTableCell>{renderValue(value.newValue)}</StyledTableCell>
                <StyledTableCell>{formatUser(value.fullName)}</StyledTableCell>
              </TableRow>
            ))
          )}
        </TableBodyStyled>
      </Table>
    </StyledTableContainer>
  )
}
